/**
 * Meta Pixel + origen de cada alta (UTM) para la campaña de Gastify.
 *
 * - El píxel se carga solo si hay ID (VITE_META_PIXEL_ID o la constante de
 *   abajo). Sin ID todo es no-op: el sitio funciona igual.
 * - El evento que optimiza la campaña es CompleteRegistration (+ StartTrial),
 *   disparado cuando se crea la empresa en el paso 2 del registro, o sea cuando
 *   la persona ya empezó la prueba gratis. No se dispara en el click del botón:
 *   si se dispara antes, Meta optimiza para gente que abandona el formulario.
 * - Los UTM se guardan al entrar al sitio y se adjuntan al evento interno
 *   'signup', para saber qué altas vinieron de la publicidad.
 */

// Pegar acá el ID del píxel "Gastify" (o definir VITE_META_PIXEL_ID en el build).
const GASTIFY_META_PIXEL_ID_FALLBACK = '1325565130647771'; // Dataset "Gastify Web" (portfolio Impulza)

export const getMetaPixelId = () =>
  String(import.meta.env?.VITE_META_PIXEL_ID || GASTIFY_META_PIXEL_ID_FALLBACK || '').trim();

const ATTRIBUTION_KEY = 'gastify_attribution_v1';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

const hasWindow = () => typeof window !== 'undefined' && typeof document !== 'undefined';

export function initMetaPixel(pixelId = getMetaPixelId()) {
  if (!hasWindow() || !pixelId || window.fbq) return false;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  return true;
}

export function trackPixel(event, params) {
  try {
    if (hasWindow() && typeof window.fbq === 'function') {
      if (params) window.fbq('track', event, params);
      else window.fbq('track', event);
    }
  } catch {
    // El píxel nunca debe romper el registro.
  }
}

// Paso 1 del registro: usuario creado, todavía sin empresa. Evento estándar
// Lead para que la campaña pueda optimizar por él si las altas completas son
// pocas.
export function trackAccountCreated() {
  trackPixel('Lead', { content_name: 'registro-gastify-paso-1' });
}

// Alta completa: la persona creó su empresa y arrancó la prueba gratis.
export function trackTrialStarted({ plan } = {}) {
  trackPixel('CompleteRegistration', { content_name: 'registro-gastify', status: 'trial', plan });
  trackPixel('StartTrial', { value: 0, currency: 'ARS', predicted_ltv: 0 });
}

// ---------- Origen (UTM) ----------
// Una visita con UTM pisa lo guardado (último clic atribuible); una visita sin
// UTM no pisa nada (navegación interna o vuelta directa).
export function captureAttribution(search = hasWindow() ? window.location.search : '') {
  try {
    const params = new URLSearchParams(search || '');
    const utm = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 120);
    });
    if (!Object.keys(utm).length) return null;
    const payload = {
      ...utm,
      landing_path: hasWindow() ? window.location.pathname : null,
      referrer: hasWindow() ? (document.referrer || null) : null,
      at: Date.now(),
    };
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return null;
  }
}

export function getAttribution() {
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
